import { DashboardCard } from "@dashboard/components/Card";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { SaleCollectionProductsQuery, useSaleCollectionProductsQuery } from "@dashboard/graphql";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Button, Skeleton, Text, vars } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

import ProductDiscountPricesDialog from "./ProductDiscountPricesDialog";

type ProductNode = NonNullable<
  NonNullable<SaleCollectionProductsQuery["collection"]>["products"]
>["edges"][number]["node"];

interface SaleCollectionDiscountsProps {
  collectionId: string;
}

export const SaleCollectionDiscounts = ({ collectionId }: SaleCollectionDiscountsProps) => {
  const { data, loading } = useSaleCollectionProductsQuery({
    variables: { id: collectionId, first: 100 },
    fetchPolicy: "cache-and-network",
  });
  const [activeProduct, setActiveProduct] = useState<ProductNode | null>(null);

  const products = mapEdgesToItems(data?.collection?.products) ?? [];

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          <FormattedMessage
            defaultMessage="Discount prices"
            id="vbIC5o"
            description="sale collection discounts, section header"
          />
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Text size={2} color="default2">
          <FormattedMessage
            defaultMessage="Set discounted prices for products in the Sale collection. Assign products above, then set each product's discount price per variant and channel here."
            id="xGSRUa"
            description="sale collection discounts subtitle"
          />
        </Text>
      </DashboardCard.Content>
      <ResponsiveTable>
        <TableHead>
          <TableRowLink>
            <TableCell style={{ paddingLeft: vars.spacing[6] }}>
              <Text size={2} color="default2">
                <FormattedMessage
                  defaultMessage="Product"
                  id="YPMXeU"
                  description="column header"
                />
              </Text>
            </TableCell>
            <TableCell style={{ width: 200 }} />
          </TableRowLink>
        </TableHead>
        <TableBody>
          {loading && products.length === 0 && (
            <TableRowLink>
              <TableCell colSpan={2}>
                <Skeleton />
              </TableCell>
            </TableRowLink>
          )}
          {!loading && products.length === 0 && (
            <TableRowLink>
              <TableCell colSpan={2}>
                <Text color="default2">
                  <FormattedMessage
                    defaultMessage="No products in the Sale collection yet."
                    id="ZYA638"
                  />
                </Text>
              </TableCell>
            </TableRowLink>
          )}
          {products.map(product => (
            <TableRowLink key={product.id} data-test-id={product.name}>
              <TableCell style={{ paddingLeft: vars.spacing[6] }}>
                <Text>{product.name}</Text>
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                <Button
                  variant="secondary"
                  onClick={() => setActiveProduct(product)}
                  data-test-id="set-discount-price"
                >
                  <FormattedMessage
                    defaultMessage="Set discount price"
                    id="nt6z/U"
                    description="button"
                  />
                </Button>
              </TableCell>
            </TableRowLink>
          ))}
        </TableBody>
      </ResponsiveTable>

      <ProductDiscountPricesDialog
        open={activeProduct !== null}
        product={activeProduct}
        onClose={() => setActiveProduct(null)}
      />
    </DashboardCard>
  );
};

export default SaleCollectionDiscounts;
